import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Skeleton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { showAlert } from "../../../../Services/Shared.service";
import { getPackageDriverPayoutByPackageId, getPackageDriverPayoutByCorporateClientId, deletePackagePayoutMapping } from "../../../../Services/Payout.service";
import { GREEN_BUTTON_STYLE } from "../../../../styles/CommonStyles";
import CreateEditPayoutMappingForm from "../../../Payout/Forms/CreateEditPayoutMappingForm";
import CreateEditPayoutForm from "../../../Payout/Forms/CreateEditPayoutForm";
import DeleteConfirmDialog from "../../../Common/Component/DeleteConfirmDialog";
import { formatTimeFromSeconds } from "../../../../utils/payoutUtils";
import { getRideTypeLabel } from "../../../../Constant/serviceTypeConstants";

function PayoutRow({ row, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const payout = row.driverPayout || {};

  return (
    <>
      <TableRow>
        {/* Expand / Collapse Arrow */}
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell
          sx={{
            textAlign: "center",
            fontSize: "1.11vw",
            color: "#39B54A",
            fontWeight: "600",
            padding: "0.83vw",
          }}
        >
          {payout.name || "-"}
        </TableCell>
        <TableCell
          align="center"
          sx={{ fontSize: "1.11vw", fontWeight: "400", padding: "0.83vw" }}
        >
          {getRideTypeLabel(row.rideType)}
        </TableCell>
        <TableCell
          align="center"
          sx={{ fontSize: "1.11vw", fontWeight: "400", padding: "0.83vw" }}
        >
          {row.percentage !== null && row.percentage !== undefined
            ? `${row.percentage}%`
            : "-"}
        </TableCell>
        <TableCell
          align="center"
          sx={{ fontSize: "1.11vw", fontWeight: "400", padding: "0.83vw" }}
        >
          {payout.carTypeName || "-"}
        </TableCell>
        <TableCell
          align="center"
          sx={{ fontSize: "1.11vw", fontWeight: "400", padding: "0.83vw" }}
        >
          {formatTimeFromSeconds(payout.timeFrom)}
        </TableCell>
        <TableCell
          align="center"
          sx={{ fontSize: "1.11vw", fontWeight: "400", padding: "0.83vw" }}
        >
          {formatTimeFromSeconds(payout.timeTo)}
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <IconButton onClick={() => onEdit(row)}>
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => onDelete(row)} sx={{ color: '#D32F2F' }}>
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>

      {/* Collapsible Section */}
      <TableRow>
        <TableCell colSpan={8} sx={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                marginY: "1.11vw",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ flex: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "0.97vw", color: "#626262" }}>
                    Initial Distance
                  </Typography>
                  <Typography sx={{ fontWeight: "500" }}>
                    {payout.initialDistance ?? "-"}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "0.97vw", color: "#626262" }}>
                    Initial Distance Charge
                  </Typography>
                  <Typography sx={{ fontWeight: "500" }}>
                    {payout.initialDistanceCharge ?? "-"}
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, textAlign: "center" }}>
                  <Typography sx={{ fontSize: "0.97vw", color: "#626262" }}>
                    Per Km Charge
                  </Typography>
                  <Typography sx={{ fontWeight: "500" }}>
                    {payout.perKmCharge ?? "-"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function DriverPayoutsTable({ packId, corporateClientId, serviceCityId }) {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [editState, setEditState] = useState({ open: false, data: null });
  const [createOpen, setCreateOpen] = useState(false);
  const [createPayoutOpen, setCreatePayoutOpen] = useState(false);
  const [deleteConfirmState, setDeleteConfirmState] = useState({ open: false, row: null, loading: false });

  useEffect(() => {
    if (packId || corporateClientId) fetchTableData();
  }, [packId, corporateClientId]);

  const fetchTableData = useCallback(() => {
    setLoading(true);
    const fetchFn = corporateClientId
      ? getPackageDriverPayoutByCorporateClientId(corporateClientId)
      : getPackageDriverPayoutByPackageId(packId);
    fetchFn
      .then((resp) => {
        setTableData(resp?.data?.list || resp?.data || []);
      })
      .catch((err) => {
        console.error("Error fetching driver payouts:", err);
        showAlert(
          "error",
          err.response?.data?.message || "Failed to fetch driver payouts"
        );
      })
      .finally(() => setLoading(false));
  }, [packId, corporateClientId]);

  const handleEdit = useCallback((row) => {
    setEditState({ open: true, data: row });
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditState({ open: false, data: null });
  }, []);

  const handleEditSuccess = useCallback(() => {
    handleCloseEdit();
    fetchTableData();
  }, [fetchTableData, handleCloseEdit]);

  // ── Attach flow ──
  const handleOpenAttach = useCallback(() => {
    setCreateOpen(true);
  }, []);

  const handleCloseAttach = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const handleAttachSuccess = useCallback(() => {
    setCreateOpen(false);
    fetchTableData();
  }, [fetchTableData]);

  // ── Create Payout flow ──
  const handleOpenCreatePayout = useCallback(() => {
    setCreatePayoutOpen(true);
  }, []);

  const handleCloseCreatePayout = useCallback(() => {
    setCreatePayoutOpen(false);
  }, []);

  const handleCreatePayoutSuccess = useCallback(() => {
    setCreatePayoutOpen(false);
    fetchTableData();
  }, [fetchTableData]);

  // ── Delete flow ──
  const handleOpenDelete = useCallback((row) => {
    setDeleteConfirmState({ open: true, row, loading: false });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const { row } = deleteConfirmState;
    setDeleteConfirmState((prev) => ({ ...prev, loading: true }));
    try {
      await deletePackagePayoutMapping(row.id);
      setDeleteConfirmState({ open: false, row: null, loading: false });
      setTableData((prevData) => prevData.filter((item) => item.id !== row.id));
    } catch (err) {
      console.error("Failed to delete payout mapping", err);
      setDeleteConfirmState((prev) => ({ ...prev, loading: false }));
    }
  }, [deleteConfirmState]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmState({ open: false, row: null, loading: false });
  }, []);

  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={50}
            sx={{ mb: 1, borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: "1.39vw", fontWeight: "600" }}>
          Driver Payouts
        </Typography>
        {/* Placeholder for Attach action */}
        <Box sx={{ display: "flex", gap: 2 }}>
          {/* <Button variant="outlined" sx={GREEN_BUTTON_STYLE} onClick={handleOpenCreatePayout}>
            CREATE PAYOUT
          </Button> */}
          <Button variant="outlined" sx={GREEN_BUTTON_STYLE} onClick={handleOpenAttach}>
            ATTACH PAYOUT
          </Button>
        </Box>
      </Box>

      {tableData.length === 0 ? (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "6px",
          }}
        >
          <Typography sx={{ color: "black" }}>
            No Driver Payouts Available
          </Typography>
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "22vw",
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    color: "#626262",
                    fontWeight: "bold",
                    fontSize: "1.11vw",
                    height: "3.89vw",
                    maxHeight: "5vw",
                    paddingX: 0,
                    paddingY: "12px",
                    backgroundColor: "#E1F4E4",
                  },
                }}
              >
                <TableCell />
                <TableCell align="center">Payout Name</TableCell>
                <TableCell align="center">Ride Type</TableCell>
                <TableCell align="center">Percentage</TableCell>
                <TableCell align="center">Car Type</TableCell>
                <TableCell align="center">Time From</TableCell>
                <TableCell align="center">Time To</TableCell>
                <TableCell align="center"></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tableData.map((row) => (
                <PayoutRow key={row.id} row={row} onEdit={handleEdit} onDelete={handleOpenDelete} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Edit Mapping Dialog ── */}
      <Dialog
        open={editState.open}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.25rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Edit Mapping
          <IconButton onClick={handleCloseEdit} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <CreateEditPayoutMappingForm
            editData={editState.data}
            serviceCityId={serviceCityId}
            existingMappings={tableData}
            onSuccess={handleEditSuccess}
            onCancel={handleCloseEdit}
          />
        </DialogContent>
      </Dialog>

      {/* ── Create Payout Dialog ── */}
      <CreateEditPayoutForm
        open={createPayoutOpen}
        onClose={handleCloseCreatePayout}
        onSuccess={handleCreatePayoutSuccess}
        editData={{ serviceCityId }}
        disableServiceCity
        standalone
      />

      {/* ── Attach Payout Dialog ── */}
      <Dialog
        open={createOpen}
        onClose={handleCloseAttach}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Attach Driver Payout Mapping
          <IconButton onClick={handleCloseAttach} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, width: "100%" }}>
          <CreateEditPayoutMappingForm
            packId={packId}
            corporateClientId={corporateClientId}
            serviceCityId={serviceCityId}
            existingPayoutIds={tableData.map((r) => r.driverPayout?.id).filter(Boolean)}
            existingMappings={tableData}
            onSuccess={handleAttachSuccess}
            onCancel={handleCloseAttach}
          />
        </DialogContent>
      </Dialog>

      {/* ── Delete Payout Mapping Confirmation ── */}
      <DeleteConfirmDialog
        open={deleteConfirmState.open}
        title="Remove Payout Mapping"
        message={
          <>
            You are about to permanently remove this payout mapping. This action cannot be undone and may impact driver payout calculations.{" "}
            <strong>Are you sure you want to proceed?</strong>
          </>
        }
        loading={deleteConfirmState.loading}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
}

export default DriverPayoutsTable;
